import { useFormContext } from 'react-hook-form';
import {
  BaseTextField,
  Card,
  ControlledDatePicker,
  ControlledTextField,
} from '../../../../shared/components';
import { financialYearLabel } from '../../../../shared/utils';

/**
 * Basic Details step — core project identity, persisted on work_jobs.
 *
 * Sanction paperwork and quotation letterhead/signature used to live here; they
 * moved to StepSanctionDetails when the form was split along the
 * work_jobs / work_jobs2 boundary.
 */
export default function StepBasicDetails() {
  const { watch } = useFormContext();

  // Read-only and outside the form: the financial year is derived from the project date
  // (April to March) and the server derives it again from `job_date` on save, so there is
  // nothing here for the payload to carry.
  const projectDate = watch('projectDate');

  return (
    <Card title='Basic Details'>
      <div className='grid grid--2-cols grid--with-divider '>
        <div className='column'>
          <ControlledTextField
            id='projectName'
            name='projectName'
            label='Name of Project'
            placeholder='Enter project name'
            rules={{
              required: 'Project name is required',
              maxLength: { value: 100, message: 'Project name cannot exceed 100 characters' },
              minLength: { value: 3, message: 'Project name should be at least 3 characters' },
            }}
            showErrorInline
          />

          <ControlledDatePicker
            label='Project date'
            id='projectDate'
            name='projectDate'
            rules={{ required: 'Project date is required' }}
          />

          <BaseTextField
            id='financialYear'
            label='Financial year'
            value={financialYearLabel(projectDate) ?? ''}
            placeholder='Set from the project date'
            disabled
            helperText='Derived from the project date — April to March.'
          />
        </div>

        <div className='column'>
          <ControlledTextField
            label='Site address'
            id='siteAddress'
            name='siteAddress'
            placeholder='Enter site address'
            rules={{
              required: 'Site address is required',
              maxLength: { value: 300, message: 'Site address cannot exceed 300 characters' },
              minLength: { value: 3, message: 'Site address should be at least 3 characters' },
            }}
            isMultiline
            rows={3}
          />

          <ControlledTextField
            label='Locality'
            id='locality'
            name='locality'
            placeholder='Enter locality'
            rules={{
              required: 'Locality is required',
              maxLength: { value: 100, message: 'Locality cannot exceed 100 characters' },
              minLength: { value: 3, message: 'Locality should be at least 3 characters' },
            }}
          />
        </div>
      </div>

      <ControlledTextField
        id='basicDetailsNotes'
        name='basicDetailsNotes'
        label='Notes'
        placeholder='Add any internal notes for this step (optional)'
        isMultiline
        rows={4}
        rules={{
          maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' },
        }}
        showCharCount
      />
    </Card>
  );
}
